<!--
  SalesFormModal: alta de factura, nota de crédito y nota de débito, con NC por descuentos, pago y recargo por condición de pago.

  ENTRADA
    Props:
      configurations     Configuración de ventas (array). La carga la vista. Sin ella rigen los valores por defecto.
    Métodos públicos (por ref):
      add(type, destination, loadCustomerDefault)
                         Abre el alta. type: 'factura' | 'notadebito' | 'notacredito'. destination: 'default' | 'discounts'.
      generateFromParams(params, query)
                         Abre el alta según los parámetros de ruta (pedido, remito, presupuesto, presupuestos, servicio,
                         proyecto, cheque rechazado o new=1). Devuelve true si consumió alguno: la vista limpia la URL.
                         Al guardar o cancelar vuelve al origen.
      creditNoteFromSalesDetail(sale_id, devolution)
                         Abre la NC desde el detalle de un comprobante. devolution: { form, lote } de la devolución en la vista.
      handlerAccessDirect(e)         Atajos de teclado. La vista registra el listener y le pasa el evento.
      loadAvailableMethodsPayment()  Refresca las condiciones de pago disponibles.
      showStaff()                    Refresca la caja del usuario.
      close()                        Cierra el formulario sin confirmar.

  SALIDA
    Eventos:
      @saved(response)            Se guardó. response.data trae el comprobante. La vista imprime, cierra lo suyo y recarga.
      @devolution-wait(boolean)   Espera de la carga de la NC desde devolución, para el botón de la vista.
    Efectos:
      Guarda con agregarComprobante. PUEDE PEDIR CAE REAL A AFIP según el punto de venta, y genera caja, remito e imputación.
      Conserva _idempotency_key entre reintentos: no regenerarla en el catch.
      Vincula la ND al cheque rechazado. Navega al origen con returnToGenerationOrigin, que también lee $route por compatibilidad.

  USOS
    Vista: <SalesFormModal ref="salesForm" :configurations="configurations" @saved="onSaleSaved($event)" @devolution-wait="modal.devolution.wait = $event" />
    Alta:         this.$refs.salesForm.add('factura')
    NC descuento: this.$refs.salesForm.add('notacredito', 'discounts')
    Desde origen: this.$refs.salesForm.generateFromParams(this.$route.params, this.$route.query)
    Hoy se invoca desde sales/staff/sales.vue: botones, atajos, detalle, ?new=1 de la paleta y rutas de generación.

  DEPENDENCIAS Y PERMISOS
    Componentes: FindObject, Detail, FormWizard, VoucherRelation, MethodPaymentSurcharge, FinanceADD, EmptyState (global).
    La impresión, el detalle y la devolución siguen en la vista.
    No controla permisos de apertura: quien lo invoca decide. Sí oculta solapas del detalle según el permiso del usuario,
    y add() rechaza al usuario vendedor que no puede emitir.
-->
<template>
  <div>
    <!-- CRUD MAIN -->
    <b-modal v-model="modal.form.active"
            no-close-on-esc
            no-close-on-backdrop
            hide-header-close
            size="xl"
            dialog-class="sales-form-modal-dialog"
            :key="modal.form._id"
            :id="modal.form._id">
      <div slot="modal-header" minimize class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
        <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
      </div>

      <b-alert show variant="info" v-if="crud.form.budget && crud.form.budget_ids" class="crud_sales_alert_from">
        <b>Desde {{crud.form.budgets_list.length}} PRESUPUESTOS: {{crud.form.budgets_list.map(b => b.point_sale.point_sale.toString().padStart(4,'0') + '-' + b.number.toString().padStart(8,'0')).join(', ')}}</b>
      </b-alert>
      <b-alert show variant="info" v-if="crud.form.budget && !crud.form.budget_ids" class="crud_sales_alert_from">
        <b>Desde {{crud.form.budget.type_voucher.name + ' ' + crud.form.budget.point_sale.point_sale.toString().padStart(4,'0') + '-' + crud.form.budget.number.toString().padStart(8,'0')}}</b>
      </b-alert>
      <b-alert show variant="info" v-if="crud.form.remits" class="crud_sales_alert_from">
        <b>Desde {{crud.form.remits.type_voucher.name + ' ' + crud.form.remits.point_sale.point_sale.toString().padStart(4,'0') + '-' + crud.form.remits.number.toString().padStart(8,'0')}}</b>
      </b-alert>
      <b-alert show variant="info" v-if="crud.form.orders" class="crud_sales_alert_from">
        <b>Desde Orden de Pedido {{crud.form.orders.points_sales.point_sale.toString().padStart(4,'0') + '-' + crud.form.orders.id.toString().padStart(8,'0')}}</b>
      </b-alert>

      <form-wizard  title=""
                    subtitle=""
                    nextButtonText="Siguiente"
                    backButtonText="Atrás"
                    finishButtonText="Guardar"
                    color="#2f353a"
                    shape="tab"
                    @on-complete="preSave"
                    class="crud-sales-wizard"
                    ref="wizard">

        <tab-content title="General" icon="fa fa-edit" :before-change="saleValidCrudGeneral">
          <b-row>
            <b-col md="4">
              <FindObject render="search"
                          type="customers"
                          @select-object="loadCustomers($event)"
                          :valueID="crud.form.customers_id"
                          :where="conditionCustomersActive"
                          :key="crud.form.customers_id"
                          :disabled="crud.form.id>0 || invoiceFrom"
                          ref="searchCustomer"
                          title="Buscador (F2)" />
            </b-col>
            <b-col md="4">
              <b-form-group label="Fecha">
                <b-form-datepicker size="sm" v-model="crud.form.date" placeholder="Fecha" local="es" :disabled="disabledDates"></b-form-datepicker>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Fecha Vencimiento">
                <b-form-datepicker size="sm" v-model="crud.form.date_expiration" placeholder="Fecha Vencimiento" local="es" :disabled="disabledDates"></b-form-datepicker>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="4">
              <FindObject render="search"
                          type="erpPointsSales"
                          auto-select-single
                          display="full_identification"
                          @select-object="loadPointsSales($event)"
                          :valueID="crud.form.points_sales_id"
                          :key="crud.form.points_sales_id"
                          :disabled="crud.form.id>0 || invoiceFrom"
                          :where="conditionPointsSalesActive" />
            </b-col>
            <b-col md="4">
              <FindObject render="search"
                          type="erpMethodsPayment"
                          auto-select-single
                          @select-object="loadMethodsPayment($event)"
                          :valueID="crud.form.methods_payment_id"
                          :key="crud.form.methods_payment_id"
                          :disabled="crud.form.id>0 || invoiceFrom || isChequeRejectionMethodPaymentLocked" />
            </b-col>
            <b-col md="4">
              <FindObject render="search"
                          type="priceList"
                          @select-object="loadPriceList($event)"
                          :valueID="crud.form.price_list_id"
                          :key="crud.form.price_list_id"
                          :where="conditionPriceListActive"
                          :disabled="crud.form.id>0 || invoiceFrom" />
            </b-col>
          </b-row>
          <b-row v-if="(parameters.haveAcopio && crud.form.selectTypeVoucher!='notacredito') || showShipping">
            <b-col md="4" v-if="parameters.haveAcopio && crud.form.selectTypeVoucher!='notacredito'">
              <FindObject render="search"
                          type="erpMethodsAcopio"
                          @select-object="loadMethodsAcopio($event)"
                          :valueID="crud.form.methods_acopio_id"
                          :key="crud.form.methods_acopio_id"
                          :disabled="crud.form.id>0 || invoiceFrom" />
            </b-col>

            <b-col md="4" v-if="showShipping">
              <FindObject render="search"
                          type="suppliers"
                          @select-object="loadShipping($event)"
                          :where="conditionSuppliersShippingActive"
                          :valueID="crud.form.shipping_id"
                          tag="Transporte"
                          :key="crud.form.shipping_id"
                          :showAllRegister="true"/>
            </b-col>
          </b-row>

          <b-row v-if="balanceCurrentAccount<0">
            <b-col>
              <b-alert show variant="danger">
                <AppIcon name="exclamation-triangle" class="mr-1" />Se encontró saldo a favor del cliente: <b>({{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(balanceCurrentAccount)}})</b>
              </b-alert>
            </b-col>
          </b-row>

          <b-row v-if="crud.form.points_sales && !crud.form.points_sales.nd">
            <b-col>
              <div v-if="crud.form.points_sales.status">
                <b-alert show variant="info" v-if="crud.form.points_sales.afip_production">
                  Punto de venta conectado a los servidores de ARCA <b>"EN PRODUCCIÓN"</b>
                </b-alert>
                <b-alert show variant="warning" v-else>
                  Punto de venta conectado a los servidores de ARCA <b>"HOMOLOGACION"</b>
                </b-alert>
              </div>
              <div v-else>
                <b-alert show variant="danger">
                  El punto de venta está <b>INACTIVO</b>
                </b-alert>
              </div>
            </b-col>
          </b-row>
        </tab-content>

        <tab-content title="Detalle" icon="fa fa-list" :before-change="saleValidCrudDetail">
          <b-row>
            <b-col lg="12" md="12" sm="12">
                <Detail :customer_id="crud.form.customers_id"
                        :price_list_id="crud.form.price_list_id"
                        :disable-customer-price-list-fallback="crud.form.disable_customer_price_list_fallback"
                        @load-detail-products="setDetailItems($event, 'products')"
                        @load-detail-products-compound="setDetailItems($event, 'productsCompound')"
                        @load-detail-services="setDetailItems($event, 'services')"
                        @load-detail-concepts="setDetailItems($event, 'concepts')"
                        :detailProducts="crud.form.detailProducts"
                        :detailProductsCompound="crud.form.detailProductsCompound"
                        :detailServices="crud.form.detailServices"
                        :detailConcepts="crud.form.detailConcepts"
                        :forceUpdate="itemForceUpdateDetail"
                        :initialTab="isChequeRejectionDebitNote ? 'concepts' : ''"
                        :hideConcepts="!access.elements.viewDetailTabConcepts"
                        :hideProducts="!access.elements.viewDetailTabProducts"
                        :hideProductsCompound="!access.elements.viewDetailTabProductsCompound"
                        :hideServices="!access.elements.viewDetailTabServices"
                        :validItemDuplicatedProduct="!parameters.multipleItems"
                        :validItemDuplicatedConcept="!parameters.multipleItems"
                        :onlyDiscountProduct="invoiceFrom"
                        :editPrice="!notEditPrice"
                        :not-iva="!!crud.form.not_iva"
                        :methodPayment="crud.form.methods_payment"/>


                <b-row class="mt-0 pt-1 pl-3 pr-3 crud-sales-detail-options" align-v="end">
                  <b-col md="6" class="mb-0" v-if="showRemits">
                    <b-form-checkbox
                      v-if="crud.form.types_vouchers && crud.form.types_vouchers.type_balance == '+'"
                      v-model="crud.form.generateRemits"
                      switch
                      size="sm"
                      class="pull-left">
                      <b>Generar Remito</b> <i>(Descuenta Stock)</i>
                    </b-form-checkbox>
                  </b-col>
                  <b-col md="6" :offset-md="showRemits ? 0 : 6" class="mb-0" v-if="showCheckNotIva">
                    <b-form-checkbox
                      v-model="crud.form.not_iva"
                      switch
                      size="sm"
                      class="pull-right">
                      <b>Precios sin IVA</b>
                    </b-form-checkbox>
                  </b-col>
                </b-row>
                <b-row class="pl-3 pr-3 pb-2 mt-2">
                  <b-col md="2" class="mb-2 mb-md-0">
                    <div class="crud-sales-stat">
                      <span class="crud-sales-stat__label">Items</span>
                      <span class="crud-sales-stat__value">{{this.getCantItemSale}}</span>
                    </div>
                  </b-col>
                  <b-col md="6" class="mb-2 mb-md-0">
                    <div class="text-center"
                         v-if="crud.form.customers && crud.form.customers.data_fiscal && crud.form.customers.data_fiscal.fiscal_conditions_id == 5">

                      <b-link href="javascript:void(0);"
                              @click="showCustomerIdentification = true"
                              v-if="!showCustomerIdentification">
                        <AppIcon name="person" class="mr-1" />Modificar datos del consumidor final
                      </b-link>

                      <b-card
                          id="container_consumer_indentification"
                          class="mb-0"
                          no-body
                          v-if="showCustomerIdentification">
                        <div slot="header" class="app-modal-header py-2">
                          <span class="app-modal-header__icon"><AppIcon name="person" /></span>
                          <h5 class="app-modal-header__title" style="font-size:14px;">Datos Consumidor Final</h5>
                        </div>

                        <b-row class="pl-3 pr-3 pt-1 pb-2">
                          <b-col md="6">
                            <b-form-group label="Nombre y Apellido" class="mb-0">
                              <b-form-input type="text"
                                            size="sm"
                                            v-model="crud.form.customer_name"
                                            required
                                            placeholder="Nombre y Apellido">
                              </b-form-input>
                            </b-form-group>
                          </b-col>
                          <b-col md="6">
                            <b-form-group label="DNI" class="mb-0">
                              <b-form-input type="text"
                                            size="sm"
                                            v-model="crud.form.customer_document_number"
                                            required
                                            placeholder="DNI">
                              </b-form-input>
                            </b-form-group>
                          </b-col>
                          <b-col md="12" class="mt-1">
                            <b-button variant="outline-dark"
                                      size="sm"
                                      class="w-100"
                                      @click="showCustomerIdentification = false">
                              Listo
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-card>

                    </div>
                  </b-col>
                  <b-col md="4" v-if="getCurrencySale">
                    <div class="crud-sales-totals">
                      <div class="crud-sales-totals__row">
                        <span class="crud-sales-totals__label">Subtotal</span>
                        <span class="crud-sales-totals__value">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(totals.subtotal)}}</span>
                      </div>
                      <div class="crud-sales-totals__row">
                        <span class="crud-sales-totals__label">Descuento</span>
                        <div class="crud-sales-totals__discount-group">
                          <div class="crud-sales-totals__discount">
                            <app-text-input v-model="crud.form.percentage_discount"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="1"
                                            size="sm"
                                            @change="validDiscounts()"/>
                            <span class="crud-sales-totals__discount-suffix">%</span>
                          </div>
                          <span class="crud-sales-totals__value" :class="{'text-danger': parseFloat(totals.discount) > 0}">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(totals.discount)}}</span>
                        </div>
                      </div>
                      <div class="crud-sales-totals__row">
                        <span class="crud-sales-totals__label">Subtotal</span>
                        <span class="crud-sales-totals__value">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(totals.subtotal_discount)}}</span>
                      </div>
                      <div class="crud-sales-totals__row">
                        <span class="crud-sales-totals__label">IVA</span>
                        <span class="crud-sales-totals__value">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(totals.iva)}}</span>
                      </div>
                      <div class="crud-sales-totals__row crud-sales-totals__row--total">
                        <span class="crud-sales-totals__label">Total</span>
                        <span class="crud-sales-totals__value">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(totals.total)}}</span>
                      </div>
                      <div class="crud-sales-totals__row crud-sales-totals__row--total" v-if="crud.form.methods_payment && parseFloat(crud.form.methods_payment.surcharge) > 0">
                        <span class="crud-sales-totals__label">{{this.crud.form.methods_payment.name}} ({{ this.crud.form.methods_payment.surcharge }}%)</span>
                        <span class="crud-sales-totals__value text-primary">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(totals.total_surcharge)}}</span>
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row class="pl-3 pr-3 pb-1" v-if="showMethodPaymentOptions && !isChequeRejectionMethodPaymentLocked">
                  <b-col md="12" class="text-right pr-0">
                    <b-button variant="outline-info"
                              size="sm"
                              @click="showSurchargeMethodPayment()">
                      <AppIcon name="money" class="mr-1" />
                      Otras Condiciones de Pago
                    </b-button>
                  </b-col>
                </b-row>

            </b-col>
          </b-row>
        </tab-content>

        <tab-content title="Facturas" icon="fa fa-link" :before-change="saleValidCrudRelation" v-if="crud.form.selectTypeVoucher=='notacredito' || crud.form.selectTypeVoucher=='notadebito'">
          <b-row>
            <b-col v-if="true">
              <VoucherRelation  :customer_id="crud.form.customers_id"
                                :point_sale_id="crud.form.points_sales_id"
                                :details="crud.form.invoiceRelated"
                                :amount="totals.total_surcharge"
                                :currency="(crud.form.price_list && crud.form.price_list.currency) ? crud.form.price_list.currency : {code:'ARS', symbol:'$', manual: true}"
                                :singleVoucher="(crud.form.detailProducts.length>0 || crud.form.detailProductsCompound.length>0)"
                                :typeVoucher="crud.form.selectTypeVoucher"
                                :key="'invoice_related_' + itemForceUpdate"
                                @getInvoice="getInvoiceRelated($event)" />
            </b-col>
            <b-col v-else>
              <b-alert variant="warning" show>
                <h4 class="alert-heading">No se encontró la moneda</h4>
                <p>
                  Debe seleccionar una lista de precios para poder buscar una factura relacionada
                </p>
              </b-alert>
            </b-col>
          </b-row>
        </tab-content>

        <tab-content title="Observaciones" icon="fa fa-flag-checkered">
          <b-row>
            <b-col md="12">
              <b-form-group label="Observaciones">
                <app-textarea
                  v-model="crud.form.observations"
                  placeholder="Ingresar las observaciones..."
                  rows="3"
                  max-rows="6"
                  lazy
                ></app-textarea>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Observaciones Internas" description="Estas observaciones no se muestran en la impresión">
                <app-textarea
                  v-model="crud.form.observations_internal"
                  placeholder="Ingresar las observaciones internas..."
                  rows="3"
                  max-rows="6"
                  lazy
                ></app-textarea>
              </b-form-group>
            </b-col>
          </b-row>
        </tab-content>

      </form-wizard>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="cancel()">Cancelar</b-button>
      </div>
    </b-modal>

    <!-- CRUD DISCOUNTS (NOTA DE CREDITO) -->
    <b-modal v-model="modal.discounts.active"
            no-close-on-esc
            no-close-on-backdrop
            hide-header-close
            size="xl"
            dialog-class="sales-form-modal-dialog"
            :key="modal.discounts._id"
            :id="modal.discounts._id">
      <div slot="modal-header" minimize class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="percent" /></span>
        <h5 class="app-modal-header__title">{{this.modal.discounts.title}}</h5>
      </div>

      <form-wizard  title=""
                    subtitle=""
                    nextButtonText="Siguiente"
                    backButtonText="Atrás"
                    finishButtonText="Guardar"
                    color="#2f353a"
                    shape="tab"
                    @on-complete="save"
                    class="crud-sales-wizard">

        <tab-content title="General" icon="fa fa-edit" :before-change="saleValidCrudGeneralDiscounts">
          <b-row>
            <b-col md="4">
              <b-form-group label="Fecha">
                <b-form-datepicker size="sm" v-model="crud.form.date" placeholder="Fecha" local="es"></b-form-datepicker>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
           <b-col md="4">
              <FindObject render="search"
                          type="customers"
                          @select-object="loadCustomers($event)"
                          :valueID="crud.form.customers_id"
                          :where="conditionCustomersActive"
                          :key="crud.form.customers_id"
                          :disabled="crud.form.id>0 || invoiceFrom" />
            </b-col>
            <b-col md="4">
              <FindObject render="search"
                          type="erpPointsSales"
                          auto-select-single
                          display="full_identification"
                          @select-object="loadPointsSales($event)"
                          :valueID="crud.form.points_sales_id"
                          :key="crud.form.points_sales_id"
                          :disabled="crud.form.id>0 || invoiceFrom"
                          :where="conditionPointsSalesActive" />
            </b-col>
            <b-col md="4">
              <FindObject render="search"
                          type="erpMethodsPayment"
                          auto-select-single
                          @select-object="loadMethodsPayment($event)"
                          :valueID="crud.form.methods_payment_id"
                          :key="crud.form.methods_payment_id"
                          :disabled="crud.form.id>0 || invoiceFrom" />
            </b-col>
            <b-col md="12" v-if="discountsConcept.loaded">
              <b-alert show variant="light" class="mb-0" v-if="crud.form.concepts">
                Concepto: <b class="mr-1">{{crud.form.concepts.name}}</b>
                <small class="text-muted">(definido en la configuración de ventas; el IVA de cada renglón se toma de la factura descontada)</small>
              </b-alert>
              <b-alert show variant="danger" class="mb-0" v-else>
                <AppIcon name="exclamation-triangle" class="mr-1" />Falta configurar el concepto para notas de crédito por descuentos en la configuración de ventas.
              </b-alert>
            </b-col>
          </b-row>
          <b-row v-if="balanceCurrentAccount<0 && crud.form.selectTypeVoucher=='notacredito'">
            <b-col>
              <b-alert show variant="danger">
                <AppIcon name="exclamation-triangle" class="mr-1" />Se encontró saldo a favor del cliente: <b>({{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(balanceCurrentAccount)}})</b>
              </b-alert>
            </b-col>
          </b-row>

          <b-row v-if="crud.form.points_sales && !crud.form.points_sales.nd">
            <b-col>
              <div v-if="crud.form.points_sales.status">
                <b-alert show variant="info" v-if="crud.form.points_sales.afip_production">
                  Punto de venta conectado a los servidores de ARCA <b>"EN PRODUCCIÓN"</b>
                </b-alert>
                <b-alert show variant="warning" v-else>
                  Punto de venta conectado a los servidores de ARCA <b>"HOMOLOGACION"</b>
                </b-alert>
              </div>
              <div v-else>
                <b-alert show variant="danger">
                  El punto de venta está <b>INACTIVO</b>
                </b-alert>
              </div>
            </b-col>
          </b-row>
        </tab-content>

        <tab-content title="Imputación" icon="fa fa-paperclip" :before-change="saleValidCrudImputationDiscounts">
          <b-row class="mb-2" v-if="arr.pending.length">
            <b-col lg="5" md="6" sm="12">
              <b-input-group size="sm" prepend="Aplicar a todas">
                <b-form-input v-model="discountsApplyAll"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="Porcentaje"
                              @keyup.enter="applyDiscountsAll()" />
                <b-input-group-append>
                  <b-input-group-text>%</b-input-group-text>
                  <b-button variant="outline-primary" @click="applyDiscountsAll()">Aplicar</b-button>
                </b-input-group-append>
              </b-input-group>
              <small class="text-muted">Se aplica a las marcadas; si no hay ninguna marcada, a todas.</small>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="12" md="12" sm="12">
              <b-table-simple hover small caption-top responsive v-if="arr.pending.length" class="app-table app-table--compact app-table--stacked-manual crud-sales-table-items">
                <b-thead>
                  <b-tr>
                    <b-th width="7%" class="text-left">Aplicar</b-th>
                    <b-th width="10%" class="text-left">Fecha</b-th>
                    <b-th width="28%" class="text-left">Comprobante</b-th>
                    <b-th width="12%" class="text-right">Total</b-th>
                    <b-th width="12%" class="text-right">Pendiente</b-th>
                    <b-th width="18%" class="text-center">Descuento</b-th>
                    <b-th width="13%" class="text-right">Descuento c/IVA</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody v-for="(item, index) in arr.pending" :key="item.id">
                  <b-tr :key="itemForceUpdate" v-if="crud.formPending[index]">
                    <b-td data-label="Aplicar" :class="'align-middle ' + alignClass('text-left')">
                      <b-form-checkbox v-model="crud.formPending[index].check"
                                        @change="validCheck(index)"
                                        switch
                                        size="sm"
                                        class="pull-left">
                      </b-form-checkbox>
                    </b-td>
                    <b-td data-label="Fecha" :class="'align-middle ' + alignClass('text-left')">
                      {{moment(item.date).format('DD/MM/YYYY')}}
                    </b-td>
                    <b-td data-label="Comprobante" :class="'align-middle ' + alignClass('text-left')">
                      {{item.type_voucher.name + ' ' + item.point_sale.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')}}
                    </b-td>
                    <b-td data-label="Total" :class="'align-middle ' + alignClass('text-right')">
                      <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(item.amount_total)}}</b>
                    </b-td>
                    <b-td data-label="Pendiente" :class="'align-middle ' + alignClass('text-right')">
                      <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(calcVoucherPending(item))}}</b>
                    </b-td>
                    <b-td data-label="Descuento" :class="'align-middle ' + alignClass('text-center')">
                      <!-- La tabla compacta anula el padding de la celda: la separación va en el contenedor. -->
                      <div class="pl-3" v-if="crud.formPending[index].check">
                        <b-input-group size="sm" class="crud-sales-discount-input">
                          <b-input-group-prepend>
                            <b-button :variant="crud.formPending[index].mode == 'percentage' ? 'dark' : 'outline-secondary'"
                                      @click="setDiscountsMode(index, 'percentage')">%</b-button>
                            <b-button :variant="crud.formPending[index].mode == 'amount' ? 'dark' : 'outline-secondary'"
                                      @click="setDiscountsMode(index, 'amount')">$</b-button>
                          </b-input-group-prepend>
                          <app-number-input size="sm"
                                        @change="validAmount(index)"
                                        v-model="crud.formPending[index].value"
                                        :placeholder="crud.formPending[index].mode == 'percentage' ? 'Porcentaje' : 'Importe c/IVA'"
                                        class="crud-sales-item-input" />
                        </b-input-group>
                      </div>
                    </b-td>
                    <b-td data-label="Descuento c/IVA" :class="'align-middle ' + alignClass('text-right')">
                      <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(crud.formPending[index].amount_total)}}</b>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <EmptyState v-else icon="paperclip" title="No hay comprobante para imputar" />
            </b-col>
          </b-row>
          <b-row align-h="end" class="mb-2">
            <b-col lg="4" md="5" sm="12">
              <b-input-group size="sm" prepend="TOTAL A DESCONTAR">
                <b-form-input :value="Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(calc.discounts)"
                              type="text"
                              :readonly="true"
                              class="crud-sales-add-total-final">
                </b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
        </tab-content>

        <tab-content title="Observaciones" icon="fa fa-flag-checkered">
          <b-row v-if="discountsPreview">
            <b-col md="12">
              <b-table-simple small responsive class="app-table app-table--compact app-table--stacked-manual mb-3">
                <b-thead>
                  <b-tr>
                    <b-th class="text-left">Renglones de la nota de crédito</b-th>
                    <b-th width="15%" class="text-right">Neto</b-th>
                    <b-th width="15%" class="text-right">IVA</b-th>
                    <b-th width="15%" class="text-right">Total</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="(line, index) in discountsPreview.lines" :key="'nc_line_' + index">
                    <b-td data-label="Renglón" :class="'align-middle ' + alignClass('text-left')">{{line.description}}</b-td>
                    <b-td data-label="Neto" :class="'align-middle ' + alignClass('text-right')">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(line.amount_net)}}</b-td>
                    <b-td data-label="IVA" :class="'align-middle ' + alignClass('text-right')">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(line.amount_iva)}}</b-td>
                    <b-td data-label="Total" :class="'align-middle ' + alignClass('text-right')">{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(line.amount_total)}}</b-td>
                  </b-tr>
                  <b-tr>
                    <b-td data-label="Total" :class="'align-middle ' + alignClass('text-left')"><b>Total nota de crédito</b></b-td>
                    <b-td data-label="Neto" :class="'align-middle ' + alignClass('text-right')"><b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(discountsPreview.totals.amount_net)}}</b></b-td>
                    <b-td data-label="IVA" :class="'align-middle ' + alignClass('text-right')"><b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(discountsPreview.totals.amount_iva)}}</b></b-td>
                    <b-td data-label="Total" :class="'align-middle ' + alignClass('text-right')"><b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrency}).format(discountsPreview.totals.amount_total)}}</b></b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <b-form-group label="Observaciones">
                <app-textarea
                  v-model="crud.form.observations"
                  placeholder="Ingresar las observaciones..."
                  rows="3"
                  max-rows="6"
                ></app-textarea>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Observaciones Internas" description="Estas observaciones no se muestran en la impresión">
                <app-textarea
                  v-model="crud.form.observations_internal"
                  placeholder="Ingresar las observaciones internas..."
                  rows="3"
                  max-rows="6"
                  lazy
                ></app-textarea>
              </b-form-group>
            </b-col>
          </b-row>
        </tab-content>

      </form-wizard>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="cancel()">Cancelar</b-button>
      </div>
    </b-modal>

    <!-- CRUD FINANCE -->
    <b-modal v-model="modal.payment.active"
            no-close-on-backdrop
            hide-header-close
            size="md">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="money" /></span>
        <h5 class="app-modal-header__title">{{this.modal.payment.title}}</h5>
      </div>

      <b-card>
        <b-row>
          <b-col md="12">
            <FinanceADD @getFinances="storeFinances($event)"
                        v-if="crud.form.customers_id && crud.form.points_sales_id"
                        :points_sales="crud.form.points_sales"
                        :amount_impute="totals.total_surcharge"
                        :reset="finances.resetControl"
                        :default="finances.default"
                        :key="finances.forceUpdate"/>
          </b-col>
          <b-col md="12" class="mt-4">
            <b-alert show variant="info" v-if="crud.form.payment && !parseFloat(crud.form.payment.total)">
              <AppIcon name="info" class="mr-1" />Si el <b>cliente paga</b> en el momento, cargue el <b>detalle de la cobranza.</b><br><br>
              <i>En caso de no carga la cobranza, el comprobante quedará en cuenta corriente <b>pendiente de imputación</b></i>
            </b-alert>
            <div v-else>
              <b-alert show variant="success" v-if="$funciones.compareMoney(crud.form.payment.total, totals.total_surcharge) === 0">
                Comprobante Saldado
              </b-alert>
              <div v-else>
                <b-alert show variant="warning" v-if="$funciones.compareMoney(crud.form.payment.total, totals.total_surcharge) > 0">
                  <AppIcon name="exclamation-triangle" class="mr-1" />El importe recibido es mayor al total de los comprobantes imputado.<br>
                  <b>Importe Imputado</b> {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(this.totals.total_surcharge)}}<br>
                  <b>Importe Recibido</b> {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(this.crud.form.payment.total)}}<br><br>
                  <i>El saldo quedará en cuenta corriente pendiente de imputación a favor del cliente</i>
                </b-alert>
                <b-alert show variant="danger" v-if="$funciones.compareMoney(crud.form.payment.total, totals.total_surcharge) < 0">
                  <AppIcon name="exclamation-triangle" class="mr-1" />El importe recibido es menor al total de los comprobantes imputado.<br>
                  <b>Importe Imputado</b> {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(this.totals.total_surcharge)}}<br>
                  <b>Importe Recibido</b> {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencySale}).format(this.crud.form.payment.total)}}<br><br>
                  <i>El comprobante quedará en cuenta corriente pendiente de imputación</i>
                </b-alert>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="cancelPayment()">Cancelar</b-button>
        <b-button variant="dark" @click="save()" title="Finalizar (CTRL + SHIFT + ENTER)"><AppIcon name="save" class="mr-1" />Finalizar</b-button>
      </div>
    </b-modal>

    <!-- METODOS DE PAGO -->
    <b-modal v-model="modal.method_payment.active"
            no-close-on-backdrop
            hide-header-close
            size="lg">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="money" /></span>
        <h5 class="app-modal-header__title">{{this.modal.method_payment.title}}</h5>
      </div>

      <b-row>
        <b-col lg="12">
          <MethodPaymentSurcharge v-if="crud.form.price_list && crud.form.price_list.currency"
                                  :amount="totals.total"
                                  :currency="crud.form.price_list.currency.code"
                                  @setMethodPayment="setMethodPayment($event)"/>

          <MethodPaymentSurcharge v-else
                                  :amount="totals.total"
                                  currency="ARS"
                                  @setMethodPayment="setMethodPayment($event)"/>
        </b-col>
      </b-row>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.method_payment.active=false">Cancelar</b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
  import serviceAPI from '../services'
  import Error from '@/handler/error'
  import Session from '@/handler/session'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import FindObject from '@/components/inc/find/findObject'
  import Detail from '@/components/inc/cpbtDetail/detail'
  import moment from 'moment'
  import VoucherRelation from './detailInvoice'
  import MethodPaymentSurcharge from './methodPayment'
  import {FormWizard, TabContent} from 'vue-form-wizard'
  import 'vue-form-wizard/dist/vue-form-wizard.min.css'
  import Param from '@/config/parameters'
  import Function from '@/handler/funciones'
  import FinanceADD from '@/components/inc/finances/add'

  export default {
    name: 'SalesFormModal',
    components: {
      FindObject,
      Detail,
      FormWizard,
      TabContent,
      VoucherRelation,
      MethodPaymentSurcharge,
      FinanceADD,
    },
    props: {
      configurations: {
        default: null,
      },
    },
    data: () => {
      return {
        access: {
          module_id: Modules.VENTAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'sales',
          elements: {
            viewDetailTabConcepts: true,
            viewDetailTabProducts: true,
            viewDetailTabProductsCompound: true,
            viewDetailTabServices: true,
          }
        },
        parameters: {
          haveAcopio: Helper.hasParametersAccess(Param.P49),
          multipleItems: Helper.hasParametersAccess(Param.P50),
          freeAddTypeOperation: Helper.hasParametersAccess(Param.P61),
          haveNotIva: Helper.hasParametersAccess(Param.P65),
        },
        isMobileViewport: false,
        crud: {
          form: {
            id: 0,
            date: '',
            date_expiration: '',
            types_vouchers_id: 0,
            types_vouchers: null,
            letter: '',
            number: 0,
            methods_payment_id: 0,
            methods_payment: null,
            price_list_id: 0,
            price_list: null,
            disable_customer_price_list_fallback: false,
            points_sales_id: 0,
            points_sales: null,
            observations: '',
            observations_internal: '',
            staff_id: 0,
            sellers_id: 0,
            customers_id: 0,
            customers: null,
            percentage_discount: 0,
            detailProducts: [],
            detailProductsCompound: [],
            detailServices: [],
            detailConcepts: [],
            invoiceRelated: [],
            selectTypeVoucher: 'factura',
            selectDestination: 'default',
            concepts: null,
            concepts_id: 0,
            remits: null,
            remits_id: 0,
            orders: null,
            orders_id: 0,
            payment: [],
            showPayment: true,
            generateRemits: false,
            disabled: false,
            methods_acopio_id: 0,
            methods_acopio: null,
            budget_id: 0,
            budget_ids: '',
            budget: null,
            budgets_list: [],
            isBudgetStage: false,
            budgetAmountImpute: 0,
            customer_name: '',
            customer_document_number: '',
            shipping_id: 0,
            shipping: null,
            isSaveConfirmActive: false,
            services_customer_id: 0,
            projects_id: 0,
            not_iva: false,
            discountsItems: [],
            _idempotency_key: crypto.randomUUID(),
          },
          formPending: [],
          formDevolution: null,
          formDevolutionLote: null,
        },
        totals: {
          subtotal: 0,
          discount: 0,
          iva: 0,
          total: 0,
          total_surcharge: 0,
          arrIva: [],
        },
        modal: {
          form: {
            active: false,
            title: '',
          },
          discounts: {
            active: false,
            title: '',
          },
          payment: {
            active: false,
            title: '',
          },
          method_payment: {
            active: false,
            title: ''
          },
          devolution: {
            wait: false,
          },
        },
        arr: {
          pending: [],
          financesCash: [],
        },
        itemForceUpdate: 0,
        itemForceUpdateDetail: 0,
        calc: {
          discounts: 0,
        },
        // NC por descuentos: concepto configurado y cálculo del servidor.
        discountsConcept: {
          loaded: false,
        },
        discountsApplyAll: '',
        discountsPreview: null,
        balanceCurrentAccount: 0,
        showCustomerIdentification: true,
        availableMethodPaymentCount: 0,
        isSaving: false,
        generationOrigin: null,
        chequeRejectionMethodPaymentLocked: false,
        finances: {
          default: null,
          staff: null,
          forceUpdate: 0,
          resetControl: false,
        }
      }
    },
    created () {
      this.access.elements.viewDetailTabConcepts = Helper.hasAccessElement(this.access, 'view_detail_tab_concepts')
      this.access.elements.viewDetailTabProducts = Helper.hasAccessElement(this.access, 'view_detail_tab_products')
      this.access.elements.viewDetailTabProductsCompound = Helper.hasAccessElement(this.access, 'view_detail_tab_products_compound')
      this.access.elements.viewDetailTabServices = Helper.hasAccessElement(this.access, 'view_detail_tab_services')
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    computed: {
      canInvoice() {
        return Helper.canInvoice()
      },
      conditionSuppliersShippingActive(){
        var condition = []
        condition.push({field: 'active', condition: true})
        condition.push({field: 'shipping', condition: true})
        return condition;
      },
      showShipping() {
        if(this.configurations) {
          return this.configurations[6].value_boolean
        } else {
          return false
        }
      },
      devolutionForConcepts() {
        if(this.configurations) {
          return this.configurations[12].value_boolean
        } else {
          return false
        }
      },
      showRemits() {
        var status = false
        var modules = Session.getSession().auth.user.permissions.modules
        modules.forEach(element => {
          if(element.id == Modules.REMITOS) {
            status = true
          }
        })
        return status
      },
      // CONDITIONS SELECT
      conditionCustomersActive(){
        return [{field: 'active', condition: true}];
      },
      conditionPriceListActive(){
        return [{field: 'active', condition: true}];
      },
      conditionPointsSalesActive() {
        return [{field: 'active', condition: true}];
      },
      // CANT / CURRENCY
      getCantItemSale() {
        var cant1 = this.crud.form.detailProducts.length
        var cant2 = this.crud.form.detailProductsCompound.length
        var cant3 = this.crud.form.detailServices.length
        var cant4 = this.crud.form.detailConcepts.length

        return parseFloat(cant1 + cant2 + cant3 + cant4)
      },
      getCurrencySale() {
        var currency = 'ARS'
        if(this.crud.form.price_list) {
          if(this.crud.form.price_list.currency) {
            currency = this.crud.form.price_list.currency.code
          }
        }
        return currency
      },
      // CONFIGURACION
      getCurrency() {
        return 'ARS'
      },
      // INVOICE DESDE
      invoiceFrom() {
        var status = false
        if(this.crud.form.remits_id) {
          status = true
        }
        if(this.crud.form.orders_id) {
          status = true
        }
        if(this.crud.form.budget_id) {
          status = false
        }
        if(this.crud.form.disabled) {
          status = true
        }
        return status
      },
      // VALUES DEFAULT
      customersDefault() {
        if(this.configurations) {
          return parseInt(this.configurations[1].value_number);
        } else {
          return 0;
        }
      },
      activeReceipts() {
        if(this.configurations) {
          return this.configurations[4].value_boolean;
        } else {
          return false;
        }
      },
      notEditPrice() {
        if(this.configurations) {
          return this.configurations[5].value_boolean;
        } else {
          return false;
        }
      },
      disabledDates() {
        if(this.configurations) {
          return this.configurations[7].value_boolean
        } else {
          return false
        }
      },
      // ND por cheque rechazado (Finanzas): condición de pago fija (Ventas › Configuración ID22)
      // y detalle en conceptos
      isChequeRejectionDebitNote() {
        return !!(this.generationOrigin && this.generationOrigin.type == 'chequeRejection')
      },
      // solo se bloquea si la condición vino precargada; si no, el usuario la elige
      isChequeRejectionMethodPaymentLocked() {
        return this.isChequeRejectionDebitNote && this.chequeRejectionMethodPaymentLocked
      },
      showMethodPaymentOptions() {
        return this.availableMethodPaymentCount > 1
      },
      // REMITO
      generateRemits() {
        let result = 'default'
        if(this.configurations) {
          if(this.configurations[2].value_string == 'always-remit') {
            result = 'si';
          }
          if(this.configurations[2].value_string == 'never-remit') {
            result = 'no';
          }
        }
        return result
      },
      // IVA
      showCheckNotIva() {
        let status = false
        let codeAfipRI = 1 // Responsable Inscripto

        const haveNotIva = this.parameters.haveNotIva

        const pointsSales = this.crud.form.points_sales ? this.crud.form.points_sales : null
        const business = pointsSales ? pointsSales.business : null

        if(haveNotIva && business) {
          if(
            (business.fiscal_conditions_id == codeAfipRI && pointsSales.nd) ||
            (business.fiscal_conditions_id != codeAfipRI)
          ) {
            status = true
          }
        }

        return status;
      }
    },
    watch: {
      'modal.devolution.wait': function (value) {
        this.$emit('devolution-wait', value)
      },
      'crud.form.customers_id': function (newQuestion, oldQuestion) {
        this.searchVoucherByCustomer()

        if(this.crud.form.selectDestination == 'discounts') {
          this.obtenerVouchersPendingImputarByCustomers()
          this.calc.discounts = 0
        }
      },
      'crud.form.points_sales_id': function (newQuestion, oldQuestion) {
        this.searchVoucherByCustomer()

        if(this.crud.form.selectDestination == 'discounts') {
          this.obtenerVouchersPendingImputarByCustomers()
          this.calc.discounts = 0
        }
      },
      'crud.form.price_list_id': function (newQuestion, oldQuestion) {
        if(newQuestion !== oldQuestion) {
          this.forceUpdateItemDetail()
        }
      },
      'crud.form.disable_customer_price_list_fallback': function (newQuestion, oldQuestion) {
        if(newQuestion !== oldQuestion) {
          this.forceUpdateItemDetail()
        }
      },
      'crud.form.detailProducts': function (newQuestion, oldQuestion) {
        this.searchTotalsTemporary()
      },
      'crud.form.detailProductsCompound': function (newQuestion, oldQuestion) {
        this.searchTotalsTemporary()
      },
      'crud.form.detailServices': function (newQuestion, oldQuestion) {
        this.searchTotalsTemporary()
      },
      'crud.form.detailConcepts': function (newQuestion, oldQuestion) {
        this.searchTotalsTemporary()
      },
      'crud.form.not_iva': function (newQuestion, oldQuestion) {
        this.searchTotalsTemporary()
      },
    },
    methods: {
      // API PUBLICA PARA LA VISTA
      close() {
        this.modal.form.active = false
      },
      generateFromParams(params, query) {
        var triggered = false
        var generationOrigin = null

        this.crud.form.orders_id = params.ordersID
        if(this.crud.form.orders_id) {
          this.invoiceFromOrders()
          generationOrigin = { type: 'orders' }
          triggered = true
        }

        this.crud.form.remits_id = params.remitsID
        if(this.crud.form.remits_id) {
          this.invoiceFromRemits()
          generationOrigin = { type: 'remits' }
          triggered = true
        }

        this.crud.form.budget_id = params.budgetID
        if(this.crud.form.budget_id) {
          let amountImpute = 0

          if(query.amount_impute){
            amountImpute = query.amount_impute
          }

          this.invoiceFromBudget(amountImpute)
          generationOrigin = { type: 'budget' }
          triggered = true
        }

        if(query.budgetsIDs) {
          this.crud.form.budget_ids = query.budgetsIDs
          this.invoiceFromMultipleBudgets(query.budgetsIDs.split(','))
          generationOrigin = { type: 'budget' }
          triggered = true
        }

        this.crud.form.services_customer_id = params.serviceCustomerID
        if(this.crud.form.services_customer_id) {
          if(query) {
            this.invoiceFromServiceCustomer(query)
          }
          generationOrigin = { type: 'services' }
          triggered = true
        }

        this.crud.form.projects_id = params.projectsID
        if(this.crud.form.projects_id) {
          if(query) {
            this.invoiceFromProjects(query)
          }
          generationOrigin = { type: 'projects', id: this.crud.form.projects_id }
          triggered = true
        }

        if(params.chequeRejectionID) {
          this.debitNoteFromChequeRejection(params.chequeRejectionID)
          generationOrigin = { type: 'chequeRejection', id: params.chequeRejectionID }
          triggered = true
        }

        // Acción directa desde la paleta de comandos: abrir el alta al
        // entrar (query.type: 'factura' por defecto, o
        // 'notacredito'/'notadebito').
        if (query.new == '1') {
          this.add(query.type || 'factura')
          triggered = true
        }

        this.generationOrigin = triggered ? generationOrigin : null
        return triggered
      },

      // RESPONSIVE
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },

      // SELECT
      loadCustomers (object) {
        if(object){
          this.crud.form.customers = object
          this.crud.form.customers_id = object.id

          if(object.data_fiscal) {
            if(object.data_fiscal.points_sales_id) {
              if(!this.crud.form.points_sales_id) {
                this.crud.form.points_sales = object.data_fiscal.points_sales
                this.crud.form.points_sales_id = object.data_fiscal.points_sales_id
              }
            }

            if(object.data_fiscal.methods_payment_id) {
              if(!this.crud.form.methods_payment_id) {
                this.crud.form.methods_payment = object.data_fiscal.methods_payment
                this.crud.form.methods_payment_id = object.data_fiscal.methods_payment_id

                let days = parseInt(object.data_fiscal.methods_payment.term)
                this.crud.form.date_expiration = moment(this.crud.form.date).add(days,'d').format('YYYY-MM-DD')
              }
            }

            if(object.data_fiscal.methods_acopio_id) {
              if(!this.crud.form.methods_acopio_id) {
                this.crud.form.methods_acopio = object.data_fiscal.methods_acopio
                this.crud.form.methods_acopio_id = object.data_fiscal.methods_acopio_id
              }
            }

            if(object.data_fiscal.shipping_id) {
              if(!this.crud.form.shipping_id) {
                this.crud.form.shipping = object.data_fiscal.shipping
                this.crud.form.shipping_id = object.data_fiscal.shipping_id
              }
            }
          }

          if(!this.crud.form.price_list_id) {
            if(!this.crud.form.disable_customer_price_list_fallback && object.price_list) {
              if(object.price_list.list) {
                this.crud.form.price_list = object.price_list.list
                this.crud.form.price_list_id = object.price_list.list.id
                this.crud.form.disable_customer_price_list_fallback = false
              }
            }
          }

          this.getBalanceCurrentAccount()
        } else {
          this.crud.form.customers = null
          this.crud.form.customers_id = 0
          this.crud.form.disable_customer_price_list_fallback = false

          this.balanceCurrentAccount = 0
        }
      },
      loadPointsSales (object) {
        if(object){
          this.crud.form.points_sales = object
          this.crud.form.points_sales_id = object.id

          // Resetear not_iva y solo cargarlo si el check debe mostrarse
          this.crud.form.not_iva = false
          // Los computed properties se evalúan síncronamente, no necesitamos $nextTick
          if(this.showCheckNotIva) {
            this.crud.form.not_iva = object.not_iva
          }

          this.getBalanceCurrentAccount()
          this.getFinancesCash()
        } else {
          this.crud.form.points_sales = null
          this.crud.form.points_sales_id = 0
          this.crud.form.not_iva = false

          this.getBalanceCurrentAccount()
        }
      },
      loadMethodsPayment (object) {
        if(object){
          this.crud.form.methods_payment = object
          this.crud.form.methods_payment_id = object.id

          let days = parseInt(object.term)
          this.crud.form.date_expiration = moment(this.crud.form.date).add(days,'d').format('YYYY-MM-DD')
        } else {
          this.crud.form.methods_payment = null
          this.crud.form.methods_payment_id = 0
        }
      },
      loadMethodsAcopio (object) {
        if(object){
          this.crud.form.methods_acopio = object
          this.crud.form.methods_acopio_id = object.id
        } else {
          this.crud.form.methods_acopio = null
          this.crud.form.methods_acopio_id = 0
        }
      },
      loadPriceList (object) {
        if(object){
          this.crud.form.price_list = object
          this.crud.form.price_list_id = object.id
          this.crud.form.disable_customer_price_list_fallback = false
        } else {
          this.crud.form.price_list = null
          this.crud.form.price_list_id = 0
          this.crud.form.disable_customer_price_list_fallback = true
        }
      },
      loadConcepts(object) {
        if(object){
          this.crud.form.concepts = object
          this.crud.form.concepts_id = object.id
        } else {
          this.crud.form.concepts = null
          this.crud.form.concepts_id = 0
        }
      },
      loadShipping (object) {
        if(object){
          this.crud.form.shipping = object
          this.crud.form.shipping_id = object.id
        } else {
          this.crud.form.shipping = null
          this.crud.form.shipping_id = 0
        }
      },

      // INVOICE FROM ORDERS / REMITS / BUDGET / SERVICES / PROJECTS
      invoiceFromOrders() {
        var result = serviceAPI.mostrarPedido(this.crud.form.orders_id);
        result.then((response) => {
          var data = response.data

          if(data.sale_id) {
            this.$awn.alert("El pedido ya se encuentra facturado");
            return false
          }

          this.add('factura','default')

          setTimeout(()=>{
            this.crud.form.orders = data
            this.crud.form.customers_id = data.customers_id
            this.crud.form.customers = data.customer
            this.crud.form.points_sales_id = data.erp_points_sales_id
            this.crud.form.points_sales = data.points_sales
            this.crud.form.methods_payment_id = data.methods_payment_id
            this.crud.form.methods_payment = data.method_payment
            this.crud.form.price_list_id = data.price_list_id
            this.crud.form.price_list = data.price_list
            this.crud.form.percentage_discount = data.percentage_discount

            if(this.generateRemits == 'default') {
              this.crud.form.generateRemits = true
            } else {
              if(this.generateRemits == 'si') {
                this.crud.form.generateRemits = true
              } else if (this.generateRemits == 'no') {
                this.crud.form.generateRemits = false
              }
            }

            if(data.details) {
              data.detail.forEach(element => {
                if(element.product) {
                  this.crud.form.detailProducts.push(element)
                }
              })
            }
          },1000)
        })
        .catch(error => {
          // blanqueo invoice from
          this.crud.form.orders_id = 0
          this.crud.form.orders = null

          this.$awn.alert(Error.showError(error));
        })
      },
      invoiceFromRemits() {
        var result = serviceAPI.mostrarRemito(this.crud.form.remits_id);
        result.then((response) => {
          var data = response.data

          if(data.sale_id) {
            this.$awn.alert("El remito ya se encuentra facturado");
            return false
          }

          this.add('factura','default')

          setTimeout(()=>{
            this.crud.form.remits = data
            this.crud.form.customers_id = data.customers_id
            this.crud.form.customers = data.customer
            this.crud.form.points_sales_id = data.points_sales_id
            this.crud.form.points_sales = data.point_sale

            if(data.details[0] && data.details[0].orders_detail && data.details[0].orders_detail.order) {
              var order = data.details[0].orders_detail.order

              this.crud.form.methods_payment_id = order.methods_payment_id
              this.crud.form.methods_payment = order.method_payment
              this.crud.form.price_list_id = order.price_list_id
              this.crud.form.price_list = order.price_list

              data.detail.forEach(element => {
                if(element.product) {
                  this.crud.form.detailProducts.push(element)
                }
              })
            }
          },1000)
        })
        .catch(error => {
          // blanqueo invoice from
          this.crud.form.remits_id = 0
          this.crud.form.remits = null

          this.$awn.alert(Error.showError(error));
        })
      },
      creditNoteFromSalesDetail(sale_id, devolution) {
        this.crud.formDevolution = devolution.form
        this.crud.formDevolutionLote = devolution.lote
        this.modal.devolution.wait = true
        let loader = this.$loading.show();

        var result = serviceAPI.mostrarComprobante(sale_id);
        result.then((response) => {
          var data = response.data

          this.add('notacredito','default', false)

          setTimeout(()=>{
            this.crud.form.customers_id = data.customers_id
            this.crud.form.customers = data.customer
            this.crud.form.points_sales_id = data.points_sales_id
            this.crud.form.points_sales = data.point_sale
            this.crud.form.methods_payment_id = data.methods_payment_id
            this.crud.form.methods_payment = data.method_payment
            this.crud.form.price_list_id = data.price_list_id
            this.crud.form.price_list = data.price_list
            this.crud.form.percentage_discount = data.percentage_discount
            this.crud.form.disabled = false

            if(data.detail) {
              data.detail.forEach(element => {
                if(this.crud.formDevolutionLote.enable) {

                  var addItem = false
                  this.crud.formDevolutionLote.data.forEach(element1 => {
                    if(element.itemDetail.id == element1.id) {
                      addItem = element1.check
                    }
                  });

                  if(addItem) {
                    if(element.product) {
                      if(parseFloat(element.itemDetail.quantity_remit)) {
                        element.quantity = parseFloat(element.itemDetail.quantity_remit)
                      } else {
                        element.quantity = parseFloat(element.itemDetail.quantity)
                      }
                      this.crud.form.detailProducts.push(element)
                    }
                    if(element.productCompound) {
                      if(parseFloat(element.itemDetail.quantity_remit)) {
                        element.quantity = parseFloat(element.itemDetail.quantity_remit)
                      } else {
                        element.quantity = parseFloat(element.itemDetail.quantity)
                      }
                      this.crud.form.detailProductsCompound.push(element)
                    }

                    // devuelvo conceptos
                    if(this.devolutionForConcepts) {
                      if(element.concepts) {
                        if(parseFloat(element.itemDetail.quantity_remit)) {
                          element.quantity = parseFloat(element.itemDetail.quantity_remit)
                        } else {
                          element.quantity = parseFloat(element.itemDetail.quantity)
                        }
                        this.crud.form.detailConcepts.push(element)
                      }
                    }
                    // fin
                  }
                } else {
                  if(this.crud.formDevolution.sale_detail.products_id == element.itemDetail.products_id &&
                    this.crud.formDevolution.sale_detail.products_colors_id == element.itemDetail.products_colors_id &&
                    this.crud.formDevolution.sale_detail.products_waist_id == element.itemDetail.products_waist_id &&
                    this.crud.formDevolution.sale_detail.products_color_waist_id == element.itemDetail.products_color_waist_id) {

                    if(element.product && element.itemDetail.id ==  this.crud.formDevolution.sale_detail_id) {
                      element.quantity = this.crud.formDevolution.quantity
                      this.crud.form.detailProducts.push(element)
                    }
                    if(element.productCompound && element.itemDetail.id ==  this.crud.formDevolution.sale_detail_id) {
                      element.quantity = this.crud.formDevolution.quantity
                      this.crud.form.detailProductsCompound.push(element)
                    }

                    // devuelvo conceptos
                    if(this.devolutionForConcepts) {
                      if(element.concepts && element.itemDetail.id ==  this.crud.formDevolution.sale_detail_id) {
                        element.quantity = this.crud.formDevolution.quantity
                        this.crud.form.detailConcepts.push(element)
                      }
                    }
                    // fin

                    if(this.crud.form.detailProducts.length>0 || this.crud.form.detailProductsCompound.length>0) {
                      if(!parseFloat(this.crud.formDevolution.quantity)) {
                        this.$awn.alert('La cantidad a devolver debe ser mayor a 0')
                        this.modal.devolution.wait = false
                        this.cancel(true)
                        return false
                      }

                      if(parseFloat(element.itemDetail.quantity) == parseFloat(element.itemDetail.quantity_remit)) {
                        if(parseFloat(this.crud.formDevolution.quantity)>parseFloat(element.itemDetail.quantity)) {
                          this.$awn.alert('La cantidad a devolver no puede ser mayor a la cantidad total')
                          this.modal.devolution.wait = false
                          this.cancel(true)
                          return false
                        }
                      }

                      if(parseFloat(element.itemDetail.quantity) > parseFloat(element.itemDetail.quantity_remit)) {
                        if(parseFloat(this.crud.formDevolution.quantity) > (parseFloat(element.itemDetail.quantity) - parseFloat(element.itemDetail.quantity_remit))) {
                          this.$awn.alert('La cantidad a devolver no puede ser mayor a la cantidad pendiente')
                          this.modal.devolution.wait = false
                          this.cancel(true)
                          return false
                        }
                      }
                    }
                  }
                }
              })

              setTimeout(()=>{
                this.crud.form.invoiceRelated.push({
                  amount: this.totals.total_surcharge,
                  reference: data.type_voucher.name + ' ' + data.point_sale.point_sale.toString().padStart(4,'0') + '-' + data.number.toString().padStart(8,'0'),
                  voucher: data,
                })
                this.forceUpdateItem()

                if(Function.toFixed(this.totals.total_surcharge,2) > Function.toFixed(parseFloat(data.amount_total) - parseFloat(data.amount_impute),2)) {
                  this.$awn.alert('El monto a devolver no puede ser mayor al monto imputado')
                  this.modal.devolution.wait = false
                  this.cancel(true)
                  return false
                }
              },2000)
            }
          },1000)

          loader.hide()
          this.modal.devolution.wait = false
        })
        .catch(error => {
          loader.hide()

          // blanqueo invoice from
          this.crud.form.disabled = false

          this.modal.devolution.wait = false
          this.$awn.alert(Error.showError(error));
        })
      },
      invoiceFromBudget(amountImpute) {
        var result = serviceAPI.mostrarPresupuesto(this.crud.form.budget_id);
        result.then((response) => {
          var data = response.data

          if(data.sale_id) {
            this.$awn.alert("El presupuesto ya se encuentra facturado");
            return false
          }

          this.add('factura','default')

          setTimeout(()=>{
            this.crud.form.budget = data
            this.crud.form.customers_id = data.customers_id
            this.crud.form.customers = data.customer
            this.crud.form.points_sales_id = data.points_sales_id
            this.crud.form.points_sales = data.point_sale
            this.crud.form.methods_payment_id = data.methods_payment_id
            this.crud.form.methods_payment = data.method_payment
            this.crud.form.price_list_id = data.price_list_id
            this.crud.form.price_list = data.price_list
            this.crud.form.percentage_discount = data.percentage_discount

            if(data.detail) {
              data.detail.forEach(element => {
                if(element.product) {
                  this.crud.form.detailProducts.push(element)
                }
                if(element.service) {

                  if(amountImpute) {
                    this.crud.form.isBudgetStage = true
                    this.crud.form.budgetAmountImpute = amountImpute

                    element.price = amountImpute
                    element.quantity = 1
                  } else {
                    this.crud.form.isBudgetStage = false
                    this.crud.form.budgetAmountImpute = 0
                  }

                  this.crud.form.detailServices.push(element)
                }
                if(element.concepts) {
                  this.crud.form.detailConcepts.push(element)
                }
              })
            }

            this.forceUpdateItemDetail()
          },1000)
        })
        .catch(error => {
          // blanqueo invoice from
          this.crud.form.budget_id = 0
          this.crud.form.budget = null
          this.crud.form.isBudgetStage = false
          this.crud.form.budgetAmountImpute = 0

          this.$awn.alert(Error.showError(error));
        })
      },
      invoiceFromMultipleBudgets(ids) {
        if(!ids || ids.length === 0) return

        var firstBudgetId = ids[0]
        var result = serviceAPI.mostrarPresupuesto(firstBudgetId)

        result.then((response) => {
          var firstData = response.data

          if(firstData.sale_id) {
            this.$awn.alert("El presupuesto #" + firstBudgetId + " ya se encuentra facturado")
            return false
          }

          this.add('factura','default')

          setTimeout(() => {
            this.crud.form.budget = firstData
            this.crud.form.budgets_list = [firstData]
            this.crud.form.customers_id = firstData.customers_id
            this.crud.form.customers = firstData.customer
            this.crud.form.points_sales_id = firstData.points_sales_id
            this.crud.form.points_sales = firstData.point_sale
            this.crud.form.methods_payment_id = firstData.methods_payment_id
            this.crud.form.methods_payment = firstData.method_payment
            this.crud.form.price_list_id = firstData.price_list_id
            this.crud.form.price_list = firstData.price_list
            this.crud.form.percentage_discount = firstData.percentage_discount

            var addDetails = (data) => {
              if(data.detail) {
                data.detail.forEach(element => {
                  if(element.product) {
                    this.crud.form.detailProducts.push(element)
                  }
                  if(element.service) {
                    this.crud.form.detailServices.push(element)
                  }
                  if(element.concepts) {
                    this.crud.form.detailConcepts.push(element)
                  }
                })
              }
            }

            addDetails(firstData)

            var remainingIds = ids.slice(1)
            var fetchNext = (index) => {
              if(index >= remainingIds.length) {
                this.forceUpdateItemDetail()
                return
              }
              var nextId = remainingIds[index]
              serviceAPI.mostrarPresupuesto(nextId).then((res) => {
                var nextData = res.data
                if(!nextData.sale_id) {
                  this.crud.form.budgets_list.push(nextData)
                  addDetails(nextData)
                }
                fetchNext(index + 1)
              }).catch(error => {
                this.$awn.alert(Error.showError(error))
              })
            }

            fetchNext(0)
          }, 1000)
        })
        .catch(error => {
          this.crud.form.budget_ids = ''
          this.$awn.alert(Error.showError(error))
        })
      },
      invoiceFromServiceCustomer(data) {
        this.add('factura','default')

        setTimeout(()=>{
          this.crud.form.customers_id = parseInt(data.customers_id)
          this.crud.form.customers = JSON.parse(data.customers)
          this.crud.form.points_sales_id = 0
          this.crud.form.points_sales = null
          this.crud.form.methods_payment_id = 0
          this.crud.form.methods_payment = null
          this.crud.form.price_list_id = 0
          this.crud.form.price_list = null
          this.crud.form.disable_customer_price_list_fallback = true
          this.crud.form.percentage_discount = 0

          if(data.detail) {
            JSON.parse(data.detail).forEach(element => {
              this.crud.form.detailServices.push(element)
            })
          }

          this.forceUpdateItemDetail()
        },1000)
      },
      // Nota de débito al cliente por un cheque rechazado (Finanzas › Cartera de Valores):
      // cliente del recibo de origen, renglones del cheque y los gastos del rechazo, y las
      // facturas que pagó el recibo como relacionadas. El punto de venta (electrónico o
      // no declarado) lo elige el usuario.
      debitNoteFromChequeRejection(rejectionID) {
        serviceAPI.datosNotaDebitoRechazo(rejectionID)
        .then((response) => {
          var data = response.data
          this.add('notadebito', 'default', false)

          setTimeout(()=>{
            if(data.customer) {
              this.crud.form.customers_id = data.customer.id
              this.crud.form.customers = data.customer
            }
            // condición de pago configurada para estas ND (o la del cliente), con su plazo
            this.chequeRejectionMethodPaymentLocked = !!data.methods_payment
            if(data.methods_payment) {
              this.crud.form.methods_payment = data.methods_payment
              this.crud.form.methods_payment_id = data.methods_payment.id
              this.crud.form.date_expiration = moment(this.crud.form.date).add(parseInt(data.methods_payment.term) || 0, 'd').format('YYYY-MM-DD')
            }
            this.crud.form.observations = data.observations
            // solo conceptos: no hay mercadería que descuente stock
            this.crud.form.generateRemits = false

            if(data.concept) {
              data.lines.forEach(line => {
                this.crud.form.detailConcepts.push({
                  id: 0,
                  concepts: data.concept,
                  quantity: 1,
                  price: line.price,
                  description: line.description,
                })
              })
            } else {
              this.$awn.warning('Configurá el concepto para notas de débito por cheques rechazados en Ventas › Configuración para precargar los renglones.')
            }

            this.forceUpdateItemDetail()
            this.forceUpdateItem()

            // Facturas relacionadas: se cargan cuando ya está calculado el total
            // (el componente de facturas las limpia si cambia el importe) y se
            // reparte el total de la ND, sin superar el total de cada factura.
            setTimeout(()=>{
              var remaining = this.$funciones.toCents(this.totals.total_surcharge)
              this.crud.form.invoiceRelated = data.related.map(element => {
                var take = Math.min(remaining, this.$funciones.toCents(element.amount_total))
                remaining = remaining - take
                return { voucher: element.voucher, reference: element.reference, amount: take / 100 }
              }).filter(element => element.amount > 0)
              this.forceUpdateItem()
            },1000)
          },1000)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      invoiceFromProjects(data) {
        this.add('factura','default')

        setTimeout(()=>{
          this.crud.form.customers_id = parseInt(data.customers_id)
          this.crud.form.customers = JSON.parse(data.customers)
          this.crud.form.points_sales_id = 0
          this.crud.form.points_sales = null
          this.crud.form.methods_payment_id = 0
          this.crud.form.methods_payment = null
          this.crud.form.price_list_id = 0
          this.crud.form.price_list = null
          this.crud.form.disable_customer_price_list_fallback = true
          this.crud.form.percentage_discount = 0

          if(data.detail) {
            JSON.parse(data.detail).forEach(element => {
              this.crud.form.detailServices.push(element)
            })
          }

          this.forceUpdateItemDetail()
        },1000)
      },
      // AMOUNT CURRENT ACCOUNT
      getBalanceCurrentAccount() {
        var result = serviceAPI.filtrarCurrentsAccountsBalance({
          customers_id: this.crud.form.customers_id,
          points_sales_id: this.crud.form.points_sales_id,
        })

        result.then((response) => {
          var data = response.data
          this.balanceCurrentAccount = data
        })
      },

      // ABM MAIN
      add(type = 'factura', destination = 'default', loadCustomerDefault = true) {
        if(!this.canInvoice) {
          this.$awn.alert('El usuario vendedor no puede emitir comprobantes')
          return false
        }
        this.crud.form._idempotency_key = crypto.randomUUID()
        this.crud.form.id = 0
        this.crud.form.date = moment().format('YYYY-MM-DD')
        this.crud.form.date_expiration = moment().add(10,'d').format('YYYY-MM-DD')
        this.crud.form.observations = ''
        this.crud.form.observations_internal = ''
        this.crud.form.staff_id = Helper.getStaffId()
        this.crud.form.sellers_id = 0

        this.crud.form.customers_id = 0
        this.crud.form.customers = null
        if(loadCustomerDefault && this.customersDefault) {
          this.crud.form.customers_id = this.customersDefault
        }

        this.crud.form.points_sales_id = 0
        this.crud.form.points_sales = null
        this.crud.form.methods_payment_id = 0
        this.crud.form.methods_payment = null
        this.crud.form.price_list_id = 0
        this.crud.form.price_list = null
        this.crud.form.disable_customer_price_list_fallback = false
        this.crud.form.concepts_id = 0
        this.crud.form.concepts = null
        this.crud.form.percentage_discount = 0
        this.crud.form.detailProducts = []
        this.crud.form.detailProductsCompound = []
        this.crud.form.detailServices = []
        this.crud.form.detailConcepts = []
        this.crud.form.invoiceRelated = []
        this.crud.form.methods_acopio_id = 0
        this.crud.form.methods_acopio = null
        this.crud.form.customer_name = ''
        this.crud.form.customer_document_number = ''
        this.crud.form.shipping_id = 0
        this.crud.form.shipping = null

        this.crud.form.not_iva = false

        this.crud.form.payment = []
        this.financesForceUpdate()

        if(this.showRemits) {
          if(this.generateRemits == 'default') {
            this.crud.form.generateRemits = false
          } else {
            if(this.generateRemits == 'si') {
              this.crud.form.generateRemits = true
            } else if (this.generateRemits == 'no') {
              this.crud.form.generateRemits = false
            }
          }
        } else {
          if(this.generateRemits == 'default') {
            this.crud.form.generateRemits = true
          } else {
            if(this.generateRemits == 'si') {
              this.crud.form.generateRemits = true
            } else if (this.generateRemits == 'no') {
              this.crud.form.generateRemits = false
            }
          }
        }

        this.balanceCurrentAccount = 0

        if(type=='factura' && destination=='default')
          this.modal.form.title = "Nueva Factura"

        if(type=='notadebito' && destination=='default')
          this.modal.form.title = "Nueva Nota de Débito"

        if(type=='notacredito' && destination=='default')
          this.modal.form.title = "Nueva Nota de Crédito"

        if(type=='notacredito' && destination=='discounts')
          this.modal.discounts.title = "Nueva Nota de Crédito (Descuentos en Facturas)"

        this.crud.form.selectTypeVoucher = type
        this.crud.form.selectDestination = destination

        if(destination=='default') {
          this.modal.form._id = Math.random().toString(36).slice(2, 8)
          this.modal.form.active = true
        }

        if(destination=='discounts') {
          this.crud.form.discountsItems = []
          this.discountsApplyAll = ''
          this.discountsPreview = null
          this.calc.discounts = 0
          this.loadDiscountsConcept()

          this.modal.discounts._id = Math.random().toString(36).slice(2, 8)
          this.modal.discounts.active = true
        }
      },
      preSave() {
        if(this.activeReceipts) {
          if(this.crud.form.selectTypeVoucher != 'notacredito') {
            this.saveAndPayment()
          } else {
            this.save()
          }
        } else {
          this.save()
        }
      },

      save() {
        if (this.isSaving) return;

        this.crud.form.isSaveConfirmActive = true

        this.confirmDialog('¿Desea continuar con la generación del comprobante?', {
          title: 'Generar Comprobante',
          okTitle: 'GENERAR'
        })
        .then(value => {
          if (value) {
            this.isSaving = true;
            let loader = this.$loading.show();

            var result = null
            if (this.crud.form.id) {
              result = serviceAPI.editarComprobante(this.crud.form);
            } else {
              result = serviceAPI.agregarComprobante(this.crud.form);
            }

            result.then((response) => {
              var data = response.data
              this.modal.form.active = false
              this.modal.discounts.active = false

              this.modal.payment.active = false
              this.finances.resetControl = true

              // blanqueo invoice from
              this.crud.form.remits_id = 0
              this.crud.form.remits = null
              this.crud.form.orders_id = 0
              this.crud.form.orders = null
              this.crud.form.budget_id = 0
              this.crud.form.budget = null
              this.crud.form.isBudgetStage = false
              this.crud.form.budgetAmountImpute = 0
              this.crud.form.services_customer_id = 0
              this.crud.form.projects_id = 0
              this.crud.form.disabled = false

              loader.hide()
              this.isSaving = false;
              this.crud.form.isSaveConfirmActive = false
              this.$emit('saved', response)
              this.$awn.success("Comprobante generado con éxito");

              // ND por cheque rechazado: queda vinculada al rechazo. Se vuelve a Cheques
              // recién cuando el vínculo está guardado, para que la vista lo muestre.
              if(this.generationOrigin && this.generationOrigin.type == 'chequeRejection' && data && data.id) {
                serviceAPI.vincularNotaDebitoRechazo(this.generationOrigin.id, data.id)
                .catch(error => {
                  this.$awn.alert('La nota de débito se generó, pero no se pudo vincular al cheque rechazado: ' + Error.showError(error))
                })
                .then(() => {
                  this.returnToGenerationOrigin()
                })
              } else {
                this.returnToGenerationOrigin()
              }
            })
            .catch(error => {
              loader.hide()
              this.isSaving = false;
              this.crud.form.isSaveConfirmActive = false
              // Conserva la clave: si el backend creó la venta pero se perdió
              // la respuesta, el siguiente intento debe recuperar esa misma
              // venta y no generar un comprobante duplicado.
              this.$awn.alert(Error.showError(error));
            })
          } else {
            this.crud.form.isSaveConfirmActive = false
          }
        })
        .catch(error => {
          this.crud.form.isSaveConfirmActive = false

          this.$awn.alert(Error.showError(error));
        })
      },
      cancel(force=false) {
        if(force) {
          this.modal.form.active = false
          this.modal.discounts.active = false

          // blanqueo invoice from
          this.crud.form.remits_id = 0
          this.crud.form.remits = null
          this.crud.form.orders_id = 0
          this.crud.form.orders = null
          this.crud.form.budget_id = 0
          this.crud.form.budget = null
          this.crud.form.isBudgetStage = false
          this.crud.form.budgetAmountImpute = 0
          this.crud.form.services_customer_id = 0
          this.crud.form.projects_id = 0
          this.crud.form.disabled = false
        } else {
          this.confirmDialog('¿Desea cancelar el comprobante?', {
            title: 'Cancelar el Comprobante',
            icon: 'warning',
            danger: true,
            okTitle: 'SI',
            cancelTitle: 'NO'
          })
          .then(value => {
            if (value) {
              this.modal.form.active = false
              this.modal.discounts.active = false

              // blanqueo invoice from
              this.crud.form.remits_id = 0
              this.crud.form.remits = null
              this.crud.form.orders_id = 0
              this.crud.form.orders = null
              this.crud.form.budget_id = 0
              this.crud.form.budget = null
              this.crud.form.isBudgetStage = false
              this.crud.form.budgetAmountImpute = 0
              this.crud.form.services_customer_id = 0
              this.crud.form.projects_id = 0
              this.crud.form.disabled = false

              this.returnToGenerationOrigin()
            }
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error));
          })
        }
      },
      setDetailItems(event, type) {
        if(type=="products") {
          this.crud.form.detailProducts = event
        }
        if(type=="productsCompound") {
          this.crud.form.detailProductsCompound = event
        }
        if(type=="services") {
          this.crud.form.detailServices = event
        }
        if(type=="concepts") {
          this.crud.form.detailConcepts = event
        }
      },
      // STORE FINANCES
      saveAndPayment() {
        this.finances.resetControl = true

        this.modal.payment.title = "Detalle del pago"
        this.modal.payment.active = true
      },
      getFinancesCash() {
        serviceAPI.obtenerCajas().then((response) => {
          const data = response.data;
          this.arr.financesCash = [];

          const freeMode = this.parameters.freeAddTypeOperation;
          const formPS = this.crud.form.points_sales || {};
          const userCashId = this.finances.staff && this.finances.staff.cash_id;
          const pointCashId = formPS.cash_id;
          const formNd = formPS.nd;

          // Validación ND (si no está en modo libre)
          const isValidND = (cash) => {
            return freeMode || (cash.nd === formNd);
          };

          // 1. Agregar la caja del usuario si la tiene y pasa validación
          if (userCashId) {
            const userCash = data.find(cash => cash.id === userCashId && isValidND(cash));
            if (userCash) this.arr.financesCash.push(userCash);
          }

          // 2. Si no hay caja de usuario, agregar la del punto de venta si pasa validación
          if (!userCashId && pointCashId) {
            const pointCash = data.find(cash => cash.id === pointCashId && isValidND(cash));
            if (pointCash) this.arr.financesCash.push(pointCash);
          }

          // 3. Agregar cajas compartidas que pasen validación
          data.forEach(cash => {
            if (cash.shared && isValidND(cash)) {
              // Evitar duplicados si ya se agregó
              const exists = this.arr.financesCash.find(c => c.id === cash.id);
              if (!exists) this.arr.financesCash.push(cash);
            }
          });

          // 4. Si no hay caja de usuario ni del punto de venta, agregar todas que pasen validación
          if (!userCashId && !pointCashId) {
            data.forEach(cash => {
              if (isValidND(cash)) {
                const exists = this.arr.financesCash.find(c => c.id === cash.id);
                if (!exists) this.arr.financesCash.push(cash);
              }
            });
          }
        });
      },
      transformFinances(id) {
        this.financesForceUpdate()

        setTimeout(()=>{
          if(this.totals.total_surcharge) {
            let cash = this.arr.financesCash.find(cash => parseInt(cash.keyaccess) === parseInt(id));

            if(cash) {
              let cash_id = cash.id

              let item = {
                cash: {
                  detail: [
                    {
                      cash: cash,
                      cash_id: cash_id,
                      total: this.totals.total_surcharge,
                    },
                  ],
                  total: this.totals.total_surcharge,
                },
                cheques: {
                  detail: null,
                  total: 0,
                },
                total: this.totals.total_surcharge,
                totalFormat: Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrency}).format(this.totals.total_surcharge),
              }

              this.finances.resetControl = true
              this.finances.default = item

              this.$awn.info("PAGO CON: <b>" + cash.name + '</b> - <b>' + item.totalFormat + '</b>');

              this.storeFinances(item)
            } else {
              this.$awn.warning("Caja/Banco <b>NO DISPONIBLE</b>");
            }
          }
        },100)
      },
      storeFinances(values) {
        this.crud.form.payment = values
        this.crud.form.payment.totalFormat = Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencySale}).format(this.crud.form.payment.total)

        this.finances.resetControl = false
      },
      cancelPayment() {
        this.modal.payment.active=false
      },
      financesForceUpdate() {
        this.finances.forceUpdate = this.finances.forceUpdate + 1
      },
      showStaff()  {
        let staffID = Helper.getStaffId()
        var result = serviceAPI.mostrarStaff(staffID)

        result.then((response) => {
          var data = response.data
          if(data) {
            this.finances.staff = data
          } else {
            this.finances.staff = null
          }
        })
      },
      loadDiscountsConcept() {
        this.discountsConcept.loaded = false
        serviceAPI.obtenerConceptoNotaCreditoDescuentos()
        .then((response) => {
          this.loadConcepts(response.data && response.data.id ? response.data : null)
          this.discountsConcept.loaded = true
        })
        .catch(error => {
          this.loadConcepts(null)
          this.discountsConcept.loaded = true
          this.$awn.alert(Error.showError(error));
        })
      },

      // VALID
      saleValidCrudGeneral() {
        this.searchTotalsTemporary()

        if(this.crud.form.customers_id && this.crud.form.points_sales_id  && this.crud.form.methods_payment_id) {
          return true;
        } else {
          if(!this.crud.form.customers_id) {
            this.$awn.alert("No se cargó el cliente");
            return false;
          }
          if(!this.crud.form.points_sales_id) {
            this.$awn.alert("No se cargó el punto de venta");
            return false;
          }
          if(!this.crud.form.methods_payment_id) {
            this.$awn.alert("No se cargó la condición de pago");
            return false;
          }
        }
      },
      saleValidCrudDetail() {
        if( this.crud.form.detailProducts.length ||
            this.crud.form.detailProductsCompound.length ||
            this.crud.form.detailServices.length ||
            this.crud.form.detailConcepts.length) {
          return true;
        } else {
          this.$awn.alert("No se cargó ningún item");
          return false;
        }
      },
      saleValidCrudRelation() {
        if(this.crud.form.invoiceRelated.length) {
          const relatedCents = this.crud.form.invoiceRelated.reduce((total, element) => {
            return total + this.$funciones.toCents(element.amount)
          }, 0)

          if(relatedCents !== this.$funciones.toCents(this.totals.total_surcharge)) {
            this.$awn.alert("Falta imputar comprobantes");
            return false;
          } else {
            return true
          }
        } else {
          this.$awn.alert("Es obligatorio vincular una o mas factura a una nota de crédito o débito");
          return false;
        }
      },
      // VALID DESC (NC)
      saleValidCrudGeneralDiscounts() {
        if(this.crud.form.customers_id && this.crud.form.points_sales_id && this.crud.form.concepts_id) {
          return true;
        } else {
          if(!this.crud.form.customers_id) {
            this.$awn.alert("No se cargó el cliente");
            return false;
          }
          if(!this.crud.form.points_sales_id) {
            this.$awn.alert("No se cargó el punto de venta");
            return false;
          }
          if(!this.crud.form.concepts_id) {
            this.$awn.alert("Falta configurar el concepto para notas de crédito por descuentos en la configuración de ventas");
            return false;
          }
        }
      },
      saleValidCrudImputationDiscounts() {
        var checked = this.crud.formPending.filter(element => element.check)
        if(!checked.length) {
          this.$awn.alert("No puede continuar sin aplicar descuentos");
          return false;
        }
        var withoutDiscount = checked.find(element => !(parseFloat(element.amount_total) > 0))
        if(withoutDiscount) {
          this.$awn.alert("Ingresá el descuento de " + this.getVoucherPendingReference(withoutDiscount.voucher) + " o desmarcalo");
          return false;
        }

        var discountsItems = checked.map(element => ({
          sale_id: element.id,
          mode: element.mode,
          value: parseFloat(element.value),
        }))

        // El servidor calcula los renglones por alícuota y lo que imputa cada
        // factura; es el mismo cálculo que se usa al guardar.
        let loader = this.$loading.show();
        return serviceAPI.obtenerVistaPreviaNotaCreditoDescuentos({
          customers_id: this.crud.form.customers_id,
          points_sales_id: this.crud.form.points_sales_id,
          discountsItems: discountsItems,
        })
        .then((response) => {
          loader.hide()
          this.crud.form.discountsItems = discountsItems
          this.setVouchersPendingDiscounts(response.data)
          return true
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
          return false
        })
      },
      // INVOICE RELACIONADA EN NC (DISCOUNTS)
      obtenerVouchersPendingImputarByCustomers() {
        this.crud.formPending = []
        this.discountsPreview = null

        if(this.crud.form.customers_id && this.crud.form.points_sales_id) {
          var result = serviceAPI.filtrarPendienteImputar({
            customers_id:this.crud.form.customers_id,
            points_sales_id: this.crud.form.points_sales_id,
            hide_note_credit: true,
          })
          result.then((response) => {
            var data = response.data

            this.arr.pending = data
            this.arr.pending.forEach(element => {
              this.crud.formPending.push({
                check: false,
                id: element.id,
                mode: 'percentage',
                value: 0,
                amount_total: 0,
                amountPending: this.calcVoucherPending(element),
                voucher: element,
              })
            });
          })
        }
      },
      calcVoucherPending(item) {
        return parseFloat(item.amount_total) - parseFloat(item.amount_impute)
      },
      getVoucherPendingReference(item) {
        return item.type_voucher.name + ' ' + item.point_sale.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')
      },
      validCheck(index) {
        var element = this.crud.formPending[index]
        element.mode = 'percentage'
        element.value = 0
        element.amount_total = 0
        if(element.check && parseFloat(this.discountsApplyAll) > 0) {
          element.value = parseFloat(this.discountsApplyAll)
        }
        this.validAmount(index)
      },
      setDiscountsMode(index, mode) {
        var element = this.crud.formPending[index]
        if(element.mode != mode) {
          element.mode = mode
          element.value = 0
          this.validAmount(index)
        }
      },
      applyDiscountsAll() {
        var percentage = parseFloat(this.discountsApplyAll)
        if(!(percentage > 0) || percentage > 100) {
          this.$awn.alert("Ingresá un porcentaje mayor a 0 y hasta 100");
          return
        }

        var applyTo = this.crud.formPending.some(element => element.check)
          ? this.crud.formPending.filter(element => element.check)
          : this.crud.formPending
        applyTo.forEach(element => {
          element.check = true
          element.mode = 'percentage'
          element.value = percentage
        })
        this.crud.formPending.forEach((element, index) => this.validAmount(index))
      },
      // Vista previa del descuento c/IVA: porcentaje sobre el saldo pendiente o
      // importe final. El reparto por alícuota lo hace el servidor.
      validAmount(index) {
        var element = this.crud.formPending[index]
        this.discountsPreview = null

        var value = parseFloat(element.value)
        if(!element.check || !(value > 0)) {
          element.value = element.check ? (value || 0) : 0
          element.amount_total = 0
        } else if(element.mode == 'percentage') {
          if(value > 100) {
            element.value = 0
            element.amount_total = 0
            this.$awn.alert("El porcentaje de descuento no puede ser mayor a 100");
          } else {
            element.amount_total = Function.toFixed((element.amountPending * value) / 100, 2)
          }
        } else {
          if(Function.toFixed(value, 2) > Function.toFixed(element.amountPending, 2)) {
            element.value = 0
            element.amount_total = 0
            this.$awn.alert("El descuento no puede ser mayor al total pendiente de imputación");
          } else {
            element.amount_total = Function.toFixed(value, 2)
          }
        }

        this.forceUpdateItem()
        this.getAmountDiscounts()
      },
      getAmountDiscounts() {
        var discounts = 0
        if(this.crud.formPending) {
          this.crud.formPending.forEach(element => {
            if(element.check) {
              discounts = discounts + parseFloat(element.amount_total || 0)
            }
          });
        }
        this.calc.discounts = Function.toFixed(discounts, 2)
      },
      setVouchersPendingDiscounts(preview) {
        this.discountsPreview = preview
        if(preview.concept) {
          this.crud.form.concepts = preview.concept
          this.crud.form.concepts_id = preview.concept.id
        }

        // Sólo referencia: al guardar, el servidor vuelve a calcular renglones e imputaciones.
        this.crud.form.invoiceRelated = preview.related.map(element => ({
          voucher: { id: element.voucher.id },
          reference: element.reference,
          amount: element.amount,
        }))


        // totales
        this.calc.discounts = preview.totals.amount_total
        this.totals.subtotal = preview.totals.amount_net
        this.totals.discount = 0
        this.totals.subtotal_discount = preview.totals.amount_net
        this.totals.iva = preview.totals.amount_iva
        this.totals.total = preview.totals.amount_total
        this.totals.total_surcharge = preview.totals.amount_total
        this.totals.arrIva = preview.totals.arrIva
      },
      forceUpdateItem() {
        this.itemForceUpdate = this.itemForceUpdate + 1
      },
      forceUpdateItemDetail() {
        this.itemForceUpdateDetail = this.itemForceUpdateDetail + 1
      },
      // SELECT VOUCHER
      searchVoucherByCustomer() {
        if(!this.crud.form.id) {
          if(this.crud.form.customers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherByCustomer({
              'customers_id': this.crud.form.customers_id,
              'point_sale_id': this.crud.form.points_sales_id,
              'reference': this.crud.form.selectTypeVoucher,
            });
            result.then((response) => {
              this.crud.form.types_vouchers_id = response.data[0].id
              this.searchVoucher()
            })
            .catch(error => {
              this.$awn.alert(Error.showError(error));
            })
          }
        } else {
          if( this.crud.form.types_vouchers ) {
            // simple
            var titleVoucher = ''
            titleVoucher = titleVoucher + this.crud.form.types_vouchers.name
            titleVoucher = titleVoucher +  ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
            titleVoucher = titleVoucher +  '-' + this.crud.form.number.toString().padStart(8,'0')

            if(this.crud.form.points_sales && !this.crud.form.points_sales.nd) {
              if(this.crud.form.points_sales.status) {
                if(this.crud.form.points_sales.afip_production) {
                  this.modal.form.title = titleVoucher + ' (CONECTADO ARCA)'
                } else {
                  this.modal.form.title = titleVoucher + ' (CONECTADO ARCA - HOMOLOGACIÓN)'
                }
              } else {
                this.modal.form.title = titleVoucher + ' (DESCONECTADO ARCA)'
              }
            } else {
              this.modal.form.title = titleVoucher
            }

            // nc discounts
            var titleDiscounts = ''
            titleDiscounts = titleDiscounts + this.crud.form.types_vouchers.name
            titleDiscounts = titleDiscounts + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
            titleDiscounts = titleDiscounts + '-' + this.crud.form.number.toString().padStart(8,'0')

            if(this.crud.form.points_sales && !this.crud.form.points_sales.nd) {
              if(this.crud.form.points_sales.status) {
                if(this.crud.form.points_sales.afip_production) {
                  this.modal.discounts.title = titleDiscounts + ' (CONECTADO ARCA)'
                } else {
                  this.modal.discounts.title = titleDiscounts + ' (CONECTADO ARCA - HOMOLOGACIÓN)'
                }
              } else {
                this.modal.discounts.title = titleDiscounts + ' (DESCONECTADO ARCA)'
              }
            } else {
              this.modal.discounts.title = titleDiscounts
            }
          }
        }
      },
      searchVoucher() {
        if(!this.crud.form.id) {
          if(this.crud.form.customers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherById({
              'points_sales_id': this.crud.form.points_sales_id,
              'types_vouchers_id' : this.crud.form.types_vouchers_id,
            });
            result.then((response) => {
              this.crud.form.types_vouchers = response.data.type_voucher
              this.crud.form.letter = response.data.type_voucher.letter
              this.crud.form.number = response.data.number

              if( this.crud.form.types_vouchers ) {
                // simple
                var titleVoucher = ''
                titleVoucher = titleVoucher + this.crud.form.types_vouchers.name
                titleVoucher = titleVoucher +  ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
                titleVoucher = titleVoucher +  '-' + this.crud.form.number.toString().padStart(8,'0')

                if(this.crud.form.points_sales && !this.crud.form.points_sales.nd) {
                  if(this.crud.form.points_sales.status) {
                    if(this.crud.form.points_sales.afip_production) {
                      this.modal.form.title = titleVoucher + ' (CONECTADO ARCA)'
                    } else {
                      this.modal.form.title = titleVoucher + ' (CONECTADO ARCA - HOMOLOGACIÓN)'
                    }
                  } else {
                    this.modal.form.title = titleVoucher + ' (DESCONECTADO ARCA)'
                  }
                } else {
                  this.modal.form.title = titleVoucher
                }

                // nc discounts
                var titleDiscounts = ''
                titleDiscounts = titleDiscounts + this.crud.form.types_vouchers.name
                titleDiscounts = titleDiscounts + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
                titleDiscounts = titleDiscounts + '-' + this.crud.form.number.toString().padStart(8,'0')

                if(this.crud.form.points_sales && !this.crud.form.points_sales.nd) {
                  if(this.crud.form.points_sales.status) {
                    if(this.crud.form.points_sales.afip_production) {
                      this.modal.discounts.title = titleDiscounts + ' (CONECTADO ARCA)'
                    } else {
                      this.modal.discounts.title = titleDiscounts + ' (CONECTADO ARCA - HOMOLOGACIÓN)'
                    }
                  } else {
                    this.modal.discounts.title = titleDiscounts + ' (DESCONECTADO ARCA)'
                  }
                } else {
                  this.modal.discounts.title = titleDiscounts
                }
              }
            })
          }
        } else {
          if( this.crud.form.types_vouchers ) {
            // simple
            var titleVoucher = ''
            titleVoucher = titleVoucher + this.crud.form.types_vouchers.name
            titleVoucher = titleVoucher +  ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
            titleVoucher = titleVoucher +  '-' + this.crud.form.number.toString().padStart(8,'0')

            if(this.crud.form.points_sales && !this.crud.form.points_sales.nd) {
              if(this.crud.form.points_sales.status) {
                if(this.crud.form.points_sales.afip_production) {
                  this.modal.form.title = titleVoucher + ' (CONECTADO ARCA)'
                } else {
                  this.modal.form.title = titleVoucher + ' (CONECTADO ARCA - HOMOLOGACIÓN)'
                }
              } else {
                this.modal.form.title = titleVoucher + ' (DESCONECTADO ARCA)'
              }
            } else {
              this.modal.form.title = titleVoucher
            }

            // nc discounts
            var titleDiscounts = ''
            titleDiscounts = titleDiscounts + this.crud.form.types_vouchers.name
            titleDiscounts = titleDiscounts + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
            titleDiscounts = titleDiscounts + '-' + this.crud.form.number.toString().padStart(8,'0')

            if(this.crud.form.points_sales && !this.crud.form.points_sales.nd) {
              if(this.crud.form.points_sales.status) {
                if(this.crud.form.points_sales.afip_production) {
                  this.modal.discounts.title = titleDiscounts + ' (CONECTADO ARCA)'
                } else {
                  this.modal.discounts.title = titleDiscounts + ' (CONECTADO ARCA - HOMOLOGACIÓN)'
                }
              } else {
                this.modal.discounts.title = titleDiscounts + ' (DESCONECTADO ARCA)'
              }
            } else {
              this.modal.discounts.title = titleDiscounts
            }
          }
        }
      },
      searchTotalsTemporary() {
        if(this.crud.form.detailProducts || this.crud.form.detailProductsCompound || this.crud.form.detailServices || this.crud.form.detailConcepts) {
          var result = serviceAPI.obtenerVoucherTotalsTemporary({
            'header': this.crud.form,
            'detailsProducts': this.crud.form.detailProducts,
            'detailsProductsCompound': this.crud.form.detailProductsCompound,
            'detailsServices': this.crud.form.detailServices,
            'detailsConcepts': this.crud.form.detailConcepts,
            'methods_payment': this.crud.form.methods_payment,
          });
          result.then((response) => {
            var data = response.data
            this.totals.subtotal = data.amount_net
            this.totals.discount = data.amount_discount
            this.totals.subtotal_discount = data.amount_subtotal
            this.totals.iva = data.amount_iva
            this.totals.total = data.amount_total
            this.totals.total_surcharge = data.amount_total_surcharge
            this.totals.arrIva = data.arrIva
          })
        }
      },
      validDiscounts() {
        if(this.crud.form.percentage_discount=='') {
          this.crud.form.percentage_discount = 0
        } else {
          if( parseFloat(this.crud.form.percentage_discount) < 0 ||
              parseFloat(this.crud.form.percentage_discount) > 100 ) {
            this.crud.form.percentage_discount = 0
          }
        }
        this.searchTotalsTemporary()
      },
      // INVOICE RELACIONADA EN NC
      getInvoiceRelated(event) {
        this.crud.form.invoiceRelated = event
      },

      // ACCESSOS DIRECTOS
      handlerAccessDirect(e) {
        // ctrl + shift + f = 70
        if (e.ctrlKey && e.shiftKey && e.keyCode === 70) {
          e.preventDefault();
          this.add('factura')
        }

        // ctrl + shift + d = 68
        if (e.ctrlKey && e.shiftKey && e.keyCode === 68) {
          e.preventDefault();
          this.add('notadebito')
        }

        // ctrl + shift + d = 67
        if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
          e.preventDefault();
          this.add('notacredito')
        }

        // ctrl + shift + enter = 13
        if(!this.crud.form.isSaveConfirmActive && !this.isSaving) {
          if (e.ctrlKey && e.shiftKey && e.keyCode === 13) {
            e.preventDefault();

            if(this.modal.payment.active) {
              this.save()
            } else {
              if(this.$refs.wizard) {
                this.$refs.wizard.nextTab();
              }
            }
          }
        }

        // f2 = 113
        if(this.modal.form.active) {
          if (e.keyCode === 113) {
            if(this.$refs.searchCustomer) {
              this.$refs.searchCustomer.openSearch()
            }
          }
        }

        // ctrl + shift + number (0-9)
        if(this.modal.payment.active){
          let isKeyNumber = e.code.includes('Numpad') || e.code.includes('Digit')
          let isCtrl = e.ctrlKey
          let isShift = e.shiftKey

          if (isKeyNumber) {
            if(isCtrl || isShift) {
              e.preventDefault();

              let id = parseInt(e.code.slice(-1))
              if(!isNaN(id)) {
                this.transformFinances(id)
              }
            }
          }
        }
      },

      // RECARGO CONDICION DE PAGO
      loadAvailableMethodsPayment() {
        var result = serviceAPI.obtenerMetodosPagos()
        result.then((response) => {
          var data = Array.isArray(response.data) ? response.data : []
          this.availableMethodPaymentCount = data.length
        })
        .catch(() => {
          this.availableMethodPaymentCount = 0
        })
      },
      showSurchargeMethodPayment() {
        this.modal.method_payment.title = "Condiciones de Pago Disponibles"
        this.modal.method_payment.active = true
      },
      setMethodPayment(object) {
        if(object){
          this.crud.form.methods_payment = object
          this.crud.form.methods_payment_id = object.id

          let days = parseInt(object.term)
          this.crud.form.date_expiration = moment(this.crud.form.date).add(days,'d').format('YYYY-MM-DD')
        }

        this.searchTotalsTemporary()
        this.modal.method_payment.active=false
      },
      /****************
       * RETORNO *
       * **************/
      returnToGenerationOrigin() {
        var origin = this.generationOrigin

        // Compatibilidad con una navegación que todavía conserve el parámetro
        // en la URL. Normalmente checkGenerateFromParams() ya la limpia.
        if (!origin) {
          if (this.$route.params.ordersID) origin = { type: 'orders' }
          else if (this.$route.params.remitsID) origin = { type: 'remits' }
          else if (this.$route.params.budgetID) origin = { type: 'budget' }
          else if (this.$route.params.serviceCustomerID) origin = { type: 'services' }
          else if (this.$route.params.projectsID) origin = { type: 'projects', id: this.$route.params.projectsID }
        }

        this.generationOrigin = null
        if (!origin) return

        if (origin.type == 'orders') this.goOrders()
        else if (origin.type == 'remits') this.goRemits()
        else if (origin.type == 'budget') this.goBudget()
        else if (origin.type == 'services') this.goServices()
        else if (origin.type == 'projects') this.goProjects(origin.id)
        else if (origin.type == 'chequeRejection') this.$router.push({ name: 'FinancesStaffCheques' })
      },
      goOrders() {
        this.$router.push({ name: 'OrdersStaffCrudOrders' })
      },
      goRemits() {
        this.$router.push({ name: 'RemitsStaffRemits' })
      },
      goBudget()  {
        this.$router.push({ name: 'BudgetsStaffCrudBudget' })
      },
      goServices()  {
        this.$router.push({ name: 'ServicesStaffDisplayServicesCustomers' })
      },
      goProjects(id)  {
        this.$router.push({ name: 'ProjectStaffDetailHoursProjects', params: {
          projectID: id
        } })
      },
    }
  }
</script>
<style>
  .crud-sales-wizard .wizard-header {
    padding: 0px;
  }
  .crud-sales-wizard .wizard-card-footer {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 4px 0;
  }
  .crud-sales-wizard .wizard-card-footer .wizard-footer-left,
  .crud-sales-wizard .wizard-card-footer .wizard-footer-right { float: none !important; }
  .crud-sales-wizard .wizard-card-footer .wizard-footer-right { margin-left: auto; }
  .crud-sales-wizard .wizard-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 112px;
    height: 36px;
    padding: 6px 14px;
    border-width: 1px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: var(--app-font-semibold);
    box-shadow: none;
    transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn::before,
  .crud-sales-wizard .wizard-footer-right .wizard-btn::after {
    font-family: 'Material Symbols Rounded';
    font-size: 18px;
    font-weight: normal;
    font-style: normal;
    font-feature-settings: 'liga';
    line-height: 1;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn::before { content: 'arrow_back'; margin-right: 6px; }
  .crud-sales-wizard .wizard-footer-right .wizard-btn::after { content: 'arrow_forward'; margin-left: 6px; }
  .crud-sales-wizard .wizard-footer-left .wizard-btn {
    background-color: var(--app-color-surface) !important;
    border-color: var(--app-color-border-strong) !important;
    color: var(--app-color-text-secondary) !important;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn:hover {
    background-color: var(--app-color-surface-subtle) !important;
    color: var(--app-color-text) !important;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn {
    background-color: var(--app-color-primary) !important;
    border-color: var(--app-color-primary) !important;
    color: #fff !important;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn:hover { filter: brightness(.94); }
  @media (max-width: 767.98px) {
    .crud-sales-wizard .wizard-card-footer { gap: 8px; padding: 12px 0 0; }
    .crud-sales-wizard .wizard-footer-left,
    .crud-sales-wizard .wizard-footer-right { display: flex; flex: 1 1 0; }
    .crud-sales-wizard .wizard-card-footer .wizard-footer-right { margin-left: 0; }
    .crud-sales-wizard .wizard-footer-left > span,
    .crud-sales-wizard .wizard-footer-right > span { display: block; width: 100%; }
    .crud-sales-wizard .wizard-btn { width: 100%; min-width: 0; }
    .crud-sales-detail-options > [class*="col-"] + [class*="col-"] { margin-top: 8px; }
    .crud-sales-detail-options .pull-left,
    .crud-sales-detail-options .pull-right { float: none !important; }
  }
  .crud-sales-item-input {
    height: 25px;
    text-align: center;
    font-weight: bold;
    font-size: 16px;
  }
  .crud-sales-add-total-final {
    font-weight: 800 !important;
    font-size: 15px !important;
    text-align: right;
  }
  .crud-sales-table-items {
    height: 400px;
    overflow: auto;
  }
  .crud_sales_alert_from {
    padding: 5px 10px;
    margin-bottom: 0px;
  }
  .crud-sales-stat {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px 8px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
  }
  .crud-sales-stat__label {
    color: var(--app-color-text-muted);
    font-size: var(--app-text-xs);
    font-weight: var(--app-font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .crud-sales-stat__value {
    font-size: 22px;
    font-weight: var(--app-font-semibold);
    color: var(--app-color-text);
  }
  .crud-sales-totals {
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
    padding: 10px 12px;
  }
  .crud-sales-totals__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 0;
    font-size: 12px;
  }
  .crud-sales-totals__row + .crud-sales-totals__row {
    border-top: 1px dashed var(--app-color-border);
  }
  .crud-sales-totals__label {
    color: var(--app-color-text-muted);
    font-weight: var(--app-font-semibold);
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.02em;
    flex: 1 1 auto;
    min-width: 0;
  }
  .crud-sales-totals__value {
    font-weight: var(--app-font-semibold);
    text-align: right;
    white-space: nowrap;
    flex: 0 0 auto;
  }
  .crud-sales-totals__row--total {
    border-top: 2px solid var(--app-color-primary) !important;
    margin-top: 4px;
    padding-top: 8px;
  }
  .crud-sales-totals__row--total .crud-sales-totals__label,
  .crud-sales-totals__row--total .crud-sales-totals__value {
    font-size: 14px;
    color: var(--app-color-text);
  }
  .crud-sales-totals__discount-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }
  .crud-sales-totals__discount {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .crud-sales-totals__discount input {
    width: 62px;
    height: 26px;
    padding: 2px 4px;
    text-align: right;
  }
  .crud-sales-totals__discount-suffix {
    color: var(--app-color-text-muted);
    font-size: 12px;
  }
  .sales-form-modal-dialog {
    max-width: min(1400px, 95vw);
  }
</style>
